export type ContentfulImagePreset = 'icon' | 'full-width'

const CONTENTFUL_IMAGE_WIDTHS: Record<ContentfulImagePreset, number> = {
  icon: 96,
  'full-width': 1440,
}

const CONTENTFUL_IMAGE_QUALITY = 80

function isContentfulAssetUrl(url: URL): boolean {
  return (
    url.hostname === 'ctfassets.net' || url.hostname.endsWith('.ctfassets.net')
  )
}

function isSvgAsset(url: URL, contentType: string): boolean {
  return (
    contentType.toLowerCase().includes('svg') ||
    url.pathname.toLowerCase().endsWith('.svg')
  )
}

export function normalizeAssetUrl(rawUrl: unknown): string | null {
  if (typeof rawUrl !== 'string') return null

  const trimmedUrl = rawUrl.trim()
  if (trimmedUrl.length === 0) return null

  if (/^https?:\/\//i.test(trimmedUrl)) return encodeURI(trimmedUrl)
  if (trimmedUrl.startsWith('//')) return encodeURI(`https:${trimmedUrl}`)

  return encodeURI(`https://${trimmedUrl.replace(/^\/+/, '')}`)
}

export function buildContentfulImageCandidateUrls(
  rawUrl: unknown,
  contentType: string,
  preset: ContentfulImagePreset,
): string[] {
  const normalizedUrl = normalizeAssetUrl(rawUrl)
  if (normalizedUrl == null) return []

  let parsedUrl: URL
  try {
    parsedUrl = new URL(normalizedUrl)
  } catch {
    return [normalizedUrl]
  }

  if (!isContentfulAssetUrl(parsedUrl)) return [normalizedUrl]

  const width = String(CONTENTFUL_IMAGE_WIDTHS[preset])
  const webpUrl = new URL(parsedUrl)
  webpUrl.searchParams.set('w', width)
  webpUrl.searchParams.set('fm', 'webp')
  webpUrl.searchParams.set('q', String(CONTENTFUL_IMAGE_QUALITY))
  webpUrl.searchParams.delete('fl')

  const candidates = [webpUrl.toString()]

  if (isSvgAsset(parsedUrl, contentType)) {
    const pngUrl = new URL(parsedUrl)
    pngUrl.searchParams.set('w', width)
    pngUrl.searchParams.set('fm', 'png')
    pngUrl.searchParams.delete('q')
    pngUrl.searchParams.set('fl', 'png8')
    candidates.push(pngUrl.toString())
  }

  candidates.push(normalizedUrl)
  return [...new Set(candidates)]
}

export function buildContentfulImageUrl(
  rawUrl: unknown,
  contentType: string,
  preset: ContentfulImagePreset,
): string | null {
  return (
    buildContentfulImageCandidateUrls(rawUrl, contentType, preset)[0] ?? null
  )
}
